import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banknote, BookOpen, Landmark, Link2, Trash2, Wallet } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { IconButton } from '@/components/icon-button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { centsToDollarsInput, dollars, dollarsToCents, formatDateLong } from '@/lib/format';
import { useBankTransactions } from '@/lib/banking';
import {
  useClientPayments,
  useCreatePayment,
  useDeletePayment,
  type PaymentInput,
  type PaymentMethod,
} from '@/lib/payments';
import { useDeleteInvoicePayment } from '@/lib/invoices';
import type { InvoiceData, InvoicePaymentData, PaymentData } from '@/types/api';

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'check', label: 'Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'ach', label: 'ACH / bank transfer' },
  { value: 'card', label: 'Card' },
  { value: 'stripe', label: 'Stripe payout' },
  { value: 'other', label: 'Other' },
];
const METHOD_LABEL: Record<string, string> = Object.fromEntries(
  METHODS.map((m) => [m.value, m.label]),
);

/**
 * Payments received from a client, each settling one or more invoices. Handles
 * the bulk case (a single Stripe payout or check across several invoices).
 */
export function PaymentsPanel({
  clientId,
  invoices,
}: {
  clientId: number;
  invoices: InvoiceData[];
}) {
  const { data: payments } = useClientPayments(clientId);
  const del = useDeletePayment(clientId);
  const confirm = useConfirm();
  const toast = useToast();
  const [recordOpen, setRecordOpen] = useState(false);

  const openInvoices = invoices.filter(
    (i) => (i.status === 'sent' || i.status === 'partial') && i.balance_cents > 0,
  );
  const list = payments ?? [];

  // Settlements written straight onto one invoice: a check keyed from the
  // invoice menu, or a card charged through Stripe. They never appear in the
  // Payment rows above, which is why they get their own section rather than
  // being left with no surface at all.
  const direct = invoices
    .flatMap((invoice) =>
      invoice.payments
        .filter((p) => p.source !== 'payment')
        .map((payment) => ({ payment, invoice })),
    )
    .sort((a, b) => (b.payment.paid_at ?? '').localeCompare(a.payment.paid_at ?? ''));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Payments received</h3>
        <Button
          variant="secondary"
          onClick={() => setRecordOpen(true)}
          disabled={openInvoices.length === 0}
        >
          <Wallet className="size-4" /> Record payment
        </Button>
      </div>

      {list.length === 0 ? (
        <p className="rounded-md bg-[var(--color-surface-muted)] px-3 py-4 text-center text-sm text-[var(--color-foreground-subtle)]">
          {openInvoices.length === 0 && direct.length === 0
            ? 'No open invoices to apply a payment to.'
            : 'Nothing applied across invoices yet. Use Record payment for a check or Stripe payout covering more than one.'}
        </p>
      ) : (
        <Card className="divide-y divide-[var(--color-border)] overflow-hidden">
          {list.map((p) => (
            <PaymentRow
              key={p.id}
              payment={p}
              onDelete={async () => {
                const ok = await confirm({
                  title: 'Reverse this payment?',
                  body: 'The payment and its allocations are removed, and each affected invoice returns to its unpaid or partial state.',
                  confirmLabel: 'Reverse payment',
                  tone: 'danger',
                });
                if (ok) {
                  await del.mutateAsync(p.id);
                  toast.success('Payment reversed');
                }
              }}
            />
          ))}
        </Card>
      )}

      {direct.length > 0 ? (
        <DirectPayments clientId={clientId} rows={direct} />
      ) : null}

      <RecordPaymentDialog
        clientId={clientId}
        openInvoices={openInvoices}
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
      />
    </div>
  );
}

/**
 * Payments recorded against a single invoice. Removing one is the only undo for
 * a check keyed against the wrong invoice: that mistake flips the invoice to
 * paid, and a paid invoice can't be voided or edited.
 */
function DirectPayments({
  clientId,
  rows,
}: {
  clientId: number;
  rows: { payment: InvoicePaymentData; invoice: InvoiceData }[];
}) {
  const del = useDeleteInvoicePayment(clientId);
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <div className="space-y-2 pt-2">
      <h3 className="text-sm font-semibold">Recorded on an invoice</h3>
      <Card className="divide-y divide-[var(--color-border)] overflow-hidden">
        {rows.map(({ payment, invoice }) => (
          <div key={payment.id} className="flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold tabular-nums">{dollars(payment.amount_cents)}</span>
                <Badge tone="neutral">{METHOD_LABEL[payment.method ?? ''] ?? payment.method}</Badge>
                {payment.source === 'stripe' ? <Badge tone="neutral">Stripe</Badge> : null}
              </div>
              <div className="mt-0.5 text-xs text-[var(--color-foreground-subtle)]">
                {invoice.number}
                {payment.paid_at ? ` · ${formatDateLong(payment.paid_at)}` : ''}
                {payment.payer_name ? ` · ${payment.payer_name}` : ''}
                {payment.reference ? ` · ${payment.reference}` : ''}
              </div>
              {payment.removal_blocked_reason ? (
                <div className="mt-1 text-[11px] text-[var(--color-foreground-subtle)]">
                  {payment.removal_blocked_reason}
                </div>
              ) : null}
            </div>
            {payment.removable ? (
              <IconButton
                type="button"
                aria-label="Remove payment"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Remove this payment?',
                    body: `${dollars(payment.amount_cents)} comes off ${invoice.number}, which returns to unpaid or partly paid. The timeline entry and the receipt PDF go with it. Nothing is refunded, since this payment was only ever a record of money taken elsewhere.`,
                    confirmLabel: 'Remove payment',
                    tone: 'danger',
                  });
                  if (ok) {
                    await del.mutateAsync({ invoiceId: invoice.id, paymentId: payment.id });
                    toast.success('Payment removed');
                  }
                }}
              >
                <Trash2 className="size-4" />
              </IconButton>
            ) : null}
          </div>
        ))}
      </Card>
    </div>
  );
}

function PaymentRow({ payment, onDelete }: { payment: PaymentData; onDelete: () => void }) {
  const navigate = useNavigate();
  const txn = payment.bank_transaction;

  return (
    <div className="flex items-start justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold tabular-nums">{dollars(payment.amount_cents)}</span>
          <Badge tone="neutral">{METHOD_LABEL[payment.method] ?? payment.method}</Badge>
        </div>
        <div className="mt-0.5 text-xs text-[var(--color-foreground-subtle)]">
          {formatDateLong(payment.received_on)}
          {payment.reference ? ` · ${payment.reference}` : ''}
          {payment.unallocated_cents > 0
            ? ` · ${dollars(payment.unallocated_cents)} unapplied`
            : ''}
        </div>

        {/* Invoices this payment settled. */}
        <div className="mt-1 flex flex-wrap gap-1">
          {payment.allocations.map((a) => (
            <span
              key={a.id}
              className="rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[11px] tabular-nums text-[var(--color-foreground-muted)]"
            >
              {a.invoice_number ?? `#${a.invoice_id}`}: {dollars(a.amount_cents)}
            </span>
          ))}
        </div>

        {/* Links out to the bank deposit and its journal entry, if any. */}
        {txn ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px]">
            <button
              type="button"
              onClick={() => navigate(`/accounting?tab=transactions&focus=${txn.id}`)}
              className="inline-flex items-center gap-1 font-medium text-[var(--color-primary)] hover:underline"
            >
              <Landmark className="size-3" />
              Deposit · {formatDateLong(txn.txn_date)} · {dollars(txn.amount_cents)}
            </button>
            {txn.journal_entry_id ? (
              <button
                type="button"
                onClick={() =>
                  navigate(`/accounting?tab=journal&focus=${txn.journal_entry_id}`)
                }
                className="inline-flex items-center gap-1 font-medium text-[var(--color-primary)] hover:underline"
              >
                <BookOpen className="size-3" /> Journal entry
              </button>
            ) : (
              <span className="inline-flex items-center gap-1 text-[var(--color-foreground-subtle)]">
                <Link2 className="size-3" /> not yet posted
              </span>
            )}
          </div>
        ) : null}
      </div>
      <IconButton type="button" aria-label="Reverse payment" onClick={onDelete}>
        <Trash2 className="size-4" />
      </IconButton>
    </div>
  );
}

function RecordPaymentDialog({
  clientId,
  openInvoices,
  open,
  onClose,
}: {
  clientId: number;
  openInvoices: InvoiceData[];
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreatePayment(clientId);
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);

  // Recent inflow bank transactions, for linking a deposit / Stripe payout.
  const { data: bankPage } = useBankTransactions({ sort: 'date_desc' });
  const deposits = (bankPage?.data ?? []).filter((t) => t.amount_cents > 0).slice(0, 40);

  const [seeded, setSeeded] = useState(false);
  const [receivedOn, setReceivedOn] = useState(today);
  const [method, setMethod] = useState<PaymentMethod>('check');
  const [reference, setReference] = useState('');
  const [bankTxnId, setBankTxnId] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  const [rows, setRows] = useState<Record<number, { checked: boolean; amount: string }>>({});

  if (open && !seeded) {
    setReceivedOn(today);
    setMethod('check');
    setReference('');
    setBankTxnId('');
    setAmount('');
    setAmountTouched(false);
    setRows(
      Object.fromEntries(
        openInvoices.map((i) => [i.id, { checked: false, amount: centsToDollarsInput(i.balance_cents) }]),
      ),
    );
    setSeeded(true);
  }

  function close() {
    setSeeded(false);
    onClose();
  }

  const allocationCents = openInvoices.reduce((sum, i) => {
    const row = rows[i.id];
    if (!row?.checked) return sum;
    return sum + (dollarsToCents(row.amount) ?? 0);
  }, 0);

  // Keep the amount in step with the allocations until the user edits it.
  const effectiveAmountCents = amountTouched ? (dollarsToCents(amount) ?? 0) : allocationCents;

  function setRow(id: number, patch: Partial<{ checked: boolean; amount: string }>) {
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const allocations = openInvoices
      .filter((i) => rows[i.id]?.checked)
      .map((i) => ({ invoice_id: i.id, amount_cents: dollarsToCents(rows[i.id].amount) ?? 0 }))
      .filter((a) => a.amount_cents > 0);

    if (allocations.length === 0) {
      toast.error('Select at least one invoice to apply the payment to');
      return;
    }
    if (allocationCents > effectiveAmountCents) {
      toast.error('Allocations exceed the payment amount');
      return;
    }
    for (const i of openInvoices) {
      const row = rows[i.id];
      if (row?.checked && (dollarsToCents(row.amount) ?? 0) > i.balance_cents) {
        toast.error(`Allocation to ${i.number} exceeds its balance due`);
        return;
      }
    }

    const input: PaymentInput = {
      received_on: receivedOn,
      method,
      reference: reference.trim() || null,
      amount_cents: effectiveAmountCents,
      bank_transaction_id: bankTxnId ? Number(bankTxnId) : null,
      allocations,
    };

    try {
      await create.mutateAsync(input);
      toast.success('Payment recorded');
      close();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
          ?.response?.data;
      toast.error(
        'Could not record payment',
        message?.errors?.allocations?.[0] ?? message?.message ?? 'Check the amounts and try again.',
      );
    }
  }

  return (
    <Dialog open={open} onClose={close} title="Record payment" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Received on</span>
            <Input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Method</span>
            <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Reference</span>
            <Input
              placeholder="Check #, payout id, note"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Total received</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amountTouched ? amount : centsToDollarsInput(allocationCents)}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountTouched(true);
              }}
            />
          </label>
        </div>

        <label className="block space-y-1 text-sm">
          <span className="flex items-center gap-1.5 font-medium text-[var(--color-foreground-muted)]">
            <Banknote className="size-3.5" /> Link bank deposit (optional)
          </span>
          <Select value={bankTxnId} onChange={(e) => setBankTxnId(e.target.value)}>
            <option value="">Not linked</option>
            {deposits.map((t) => (
              <option key={t.id} value={t.id}>
                {t.txn_date} · {dollars(t.amount_cents)} · {t.name ?? t.merchant_name ?? 'Deposit'}
              </option>
            ))}
          </Select>
        </label>

        <div className="space-y-1.5">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
            Apply to invoices
          </div>
          <Card className="divide-y divide-[var(--color-border)] overflow-hidden">
            {openInvoices.map((i) => {
              const row = rows[i.id] ?? { checked: false, amount: '' };
              return (
                <div key={i.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={row.checked}
                    onChange={(e) => setRow(i.id, { checked: e.target.checked })}
                    className="size-4"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium tabular-nums">{i.number}</div>
                    <div className="text-xs text-[var(--color-foreground-subtle)] tabular-nums">
                      {dollars(i.balance_cents)} due · issued {formatDateLong(i.issue_date)}
                    </div>
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-28 text-right"
                    value={row.amount}
                    disabled={!row.checked}
                    onChange={(e) => setRow(i.id, { amount: e.target.value })}
                  />
                </div>
              );
            })}
          </Card>
        </div>

        <div className="ml-auto w-64 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-[var(--color-foreground-muted)]">Applied</span>
            <span className="tabular-nums">{dollars(allocationCents)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[var(--color-foreground-muted)]">Total received</span>
            <span className="tabular-nums">{dollars(effectiveAmountCents)}</span>
          </div>
          {effectiveAmountCents - allocationCents !== 0 ? (
            <div className="flex justify-between border-t border-[var(--color-border)] pt-1 text-[var(--color-foreground-subtle)]">
              <span>Unapplied</span>
              <span className="tabular-nums">{dollars(effectiveAmountCents - allocationCents)}</span>
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Record payment'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
